import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  PanelRightClose, 
  PanelRightOpen,
  Bookmark,
  Link2,
  StickyNote,
  ChevronRight,
  ExternalLink,
  Sparkles,
  Clock,
  MapPin,
  User,
  FileText,
  ArrowLeftRight,
  Download,
  X,
  TrendingUp,
  Bell
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { getSavedCases, getSavedMatches, getAllNotes } from '@/lib/api';

// Get workspace summary
const getWorkspaceSummary = async () => {
  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workspace/summary`, {
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    }
  });
  if (!response.ok) throw new Error('Failed to fetch summary');
  return response.json();
};

export const WorkspaceSidebar = ({ className = '' }) => {
  const { user, isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [savedCases, setSavedCases] = useState([]);
  const [savedMatches, setSavedMatches] = useState([]);
  const [recentNotes, setRecentNotes] = useState([]);
  const [summary, setSummary] = useState(null);
  
  // Only show for Pro/Organization users
  const isPro = user?.subscription_plan === 'professional' || user?.subscription_plan === 'organization';
  
  useEffect(() => {
    if (isOpen && isPro) {
      loadWorkspaceData();
    }
  }, [isOpen, isPro]);
  
  const loadWorkspaceData = async () => {
    setIsLoading(true);
    try {
      const [casesRes, matchesRes, notesRes, summaryRes] = await Promise.all([
        getSavedCases(null, 5).catch(() => ({ cases: [] })),
        getSavedMatches(null, 5).catch(() => ({ matches: [] })),
        getAllNotes(5).catch(() => ({ notes: [] })),
        getWorkspaceSummary().catch(() => null)
      ]);
      
      setSavedCases(casesRes.cases || []);
      setSavedMatches(matchesRes.matches || []);
      setRecentNotes(notesRes.notes || []);
      setSummary(summaryRes);
    } catch (err) {
      console.debug('Failed to load workspace data:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Don't render for non-pro users
  if (!isAuthenticated || !isPro) {
    return null;
  }
  
  return (
    <>
      {/* Toggle Button - Fixed position */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed right-0 top-1/2 -translate-y-1/2 z-40 hidden lg:flex items-center gap-1 px-2 py-3 bg-slate-800 dark:bg-slate-700 text-white rounded-l-lg shadow-lg hover:bg-slate-700 dark:hover:bg-slate-600 transition-colors ${isOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        data-testid="workspace-sidebar-toggle"
        title="Open Workspace"
      >
        <PanelRightOpen className="w-4 h-4" />
      </button>
      
      {/* Sidebar Panel */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop on mobile */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/20 z-40 lg:hidden"
              onClick={() => setIsOpen(false)}
            />
            
            {/* Sidebar */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className={`fixed right-0 top-0 h-full w-80 bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-700 shadow-2xl z-50 flex flex-col ${className}`}
              data-testid="workspace-sidebar"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-trust-blue-500" />
                  <span className="font-semibold text-slate-800 dark:text-slate-200">My Workspace</span>
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">PRO</Badge>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                >
                  <PanelRightClose className="w-4 h-4 text-slate-500" />
                </button>
              </div>
              
              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4 space-y-6">
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-2 border-trust-blue-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (
                  <>
                    {/* Quick Stats Summary */}
                    {summary && (
                      <section className="grid grid-cols-3 gap-2 pb-4 border-b border-slate-200 dark:border-slate-700">
                        <div className="text-center p-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                          <p className="text-lg font-bold text-trust-blue-600 dark:text-trust-blue-400">
                            {summary.saved_matches || 0}
                          </p>
                          <p className="text-[10px] text-slate-500 dark:text-slate-400">Matches</p>
                        </div>
                        <div className="text-center p-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                          <p className="text-lg font-bold text-amber-600 dark:text-amber-400">
                            {summary.saved_cases || 0}
                          </p>
                          <p className="text-[10px] text-slate-500 dark:text-slate-400">Cases</p>
                        </div>
                        <div className="text-center p-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                          <p className="text-lg font-bold text-purple-600 dark:text-purple-400">
                            {summary.notes || 0}
                          </p>
                          <p className="text-[10px] text-slate-500 dark:text-slate-400">Notes</p>
                        </div>
                      </section>
                    )}
                    
                    {/* Today's Activity */}
                    {summary?.today && (summary.today.new_matches > 0 || summary.today.alerts > 0) && (
                      <section className="p-3 bg-gradient-to-r from-trust-blue-50 to-indigo-50 dark:from-trust-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-trust-blue-200 dark:border-trust-blue-700">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="w-4 h-4 text-trust-blue-600 dark:text-trust-blue-400" />
                          <span className="text-xs font-semibold text-trust-blue-700 dark:text-trust-blue-300">Today's Activity</span>
                        </div>
                        <div className="flex gap-4 text-xs">
                          {summary.today.new_matches > 0 && (
                            <span className="text-slate-600 dark:text-slate-400">
                              <span className="font-medium text-trust-blue-600 dark:text-trust-blue-400">{summary.today.new_matches}</span> new match{summary.today.new_matches !== 1 ? 'es' : ''}
                            </span>
                          )}
                          {summary.today.alerts > 0 && (
                            <span className="text-slate-600 dark:text-slate-400 flex items-center gap-1">
                              <Bell className="w-3 h-3" />
                              <span className="font-medium text-amber-600 dark:text-amber-400">{summary.today.alerts}</span> alert{summary.today.alerts !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </section>
                    )}
                    
                    {/* Saved Matches */}
                    <section>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide flex items-center gap-1.5">
                          <Link2 className="w-3.5 h-3.5" />
                          Tracked Matches
                        </h3>
                        <Link to="/workspace?tab=matches" className="text-[10px] text-trust-blue-600 hover:underline">
                          View all
                        </Link>
                      </div>
                      {savedMatches.length > 0 ? (
                        <div className="space-y-2">
                          {savedMatches.slice(0, 5).map((match, idx) => (
                            <Link
                              key={match.id || idx}
                              to={`/potential-matches?highlight=${match.match_id}`}
                              className="block p-2.5 bg-slate-50 dark:bg-slate-800/50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors group"
                            >
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded bg-trust-blue-100 dark:bg-trust-blue-900/30 flex items-center justify-center flex-shrink-0">
                                  <Link2 className="w-4 h-4 text-trust-blue-600 dark:text-trust-blue-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-slate-700 dark:text-slate-300 truncate">
                                    {match.missing_person_name || 'Missing Person'} ↔ {match.unidentified_person_name || 'Unidentified'}
                                  </p>
                                  <p className="text-[10px] text-slate-500 dark:text-slate-400">
                                    Score: {match.match_score?.toFixed(0) || '—'}%
                                  </p>
                                </div>
                                <ChevronRight className="w-3.5 h-3.5 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                            </Link>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400 dark:text-slate-500 italic py-2">
                          No tracked matches yet
                        </p>
                      )}
                    </section>
                    
                    {/* Saved Cases */}
                    <section>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide flex items-center gap-1.5">
                          <Bookmark className="w-3.5 h-3.5" />
                          Saved Cases
                        </h3>
                        <Link to="/workspace?tab=cases" className="text-[10px] text-trust-blue-600 hover:underline">
                          View all
                        </Link>
                      </div>
                      {savedCases.length > 0 ? (
                        <div className="space-y-2">
                          {savedCases.slice(0, 5).map((caseItem, idx) => (
                            <Link
                              key={caseItem.id || idx}
                              to={`/case/${caseItem.case_id}`}
                              className="block p-2.5 bg-slate-50 dark:bg-slate-800/50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors group"
                            >
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                                  <User className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-slate-700 dark:text-slate-300 truncate">
                                    {caseItem.case_name || caseItem.name || 'Unnamed Case'}
                                  </p>
                                  {caseItem.location && (
                                    <p className="text-[10px] text-slate-500 dark:text-slate-400 flex items-center gap-1">
                                      <MapPin className="w-2.5 h-2.5" />
                                      <span className="truncate">{caseItem.location}</span>
                                    </p>
                                  )}
                                </div>
                                <ChevronRight className="w-3.5 h-3.5 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                            </Link>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400 dark:text-slate-500 italic py-2">
                          No saved cases yet
                        </p>
                      )}
                    </section>
                    
                    {/* Recent Notes */}
                    <section>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide flex items-center gap-1.5">
                          <StickyNote className="w-3.5 h-3.5" />
                          Recent Notes
                        </h3>
                        <Link to="/workspace?tab=notes" className="text-[10px] text-trust-blue-600 hover:underline">
                          View all
                        </Link>
                      </div>
                      {recentNotes.length > 0 ? (
                        <div className="space-y-2">
                          {recentNotes.slice(0, 3).map((note, idx) => (
                            <div
                              key={note.id || idx}
                              className="p-2.5 bg-slate-50 dark:bg-slate-800/50 rounded-lg"
                            >
                              <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-2">
                                {note.content || note.text || 'Empty note'}
                              </p>
                              <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1 flex items-center gap-1">
                                <Clock className="w-2.5 h-2.5" />
                                {note.created_at ? new Date(note.created_at).toLocaleDateString() : 'Recently'}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400 dark:text-slate-500 italic py-2">
                          No notes yet
                        </p>
                      )}
                    </section>
                  </>
                )}
              </div>
              
              {/* Quick Actions Footer */}
              <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2 font-medium">Quick Actions</p>
                <div className="grid grid-cols-2 gap-2">
                  <Link to="/workspace">
                    <Button variant="outline" size="sm" className="w-full text-xs h-8 gap-1.5">
                      <FileText className="w-3.5 h-3.5" />
                      Full Workspace
                    </Button>
                  </Link>
                  <Link to="/compare">
                    <Button variant="outline" size="sm" className="w-full text-xs h-8 gap-1.5">
                      <ArrowLeftRight className="w-3.5 h-3.5" />
                      Compare
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default WorkspaceSidebar;
